import { useId, useMergeRefs } from '@floating-ui/react';
import classnames from 'classnames';
import * as React from 'react';
import '../../utils/index.js';
import '../button/index.js';
import '../form_required_indicator/index.js';
import '../form_row/index.js';
import { Icon } from '../icons/index.js';
import '../localization/index.js';
import '../spinner/index.js';
import '../stack/index.js';
import { maxWidth, small, disabled, invalid, control, icon, appendage, input, spinner, clearButton, requiredIndicator } from './input.css.js';
import { useLocalization } from '../localization/localization.js';
import { useControlledState } from '../../utils/controlled_state.js';
import { FormRow } from '../form_row/form_row.js';
import { Stack } from '../stack/stack.js';
import { Spinner } from '../spinner/spinner.js';
import { Button } from '../button/button.js';
import { FormRequiredIndicator } from '../form_required_indicator/form_required_indicator.js';

/**
 * An input allows users to provide short text content in the context of forms.
 */
const Input = React.forwardRef(function Input({ value: controlledValue, defaultValue = '', type = 'text', label, labelClassName, labelPosition = 'block', invalid: propsInvalid = false, disabled: disabled$1 = false, loading = false, readOnly = false, required = false, autoFocus, autoComplete = 'on', size = 'medium', placeholder, icon: icon$1, maxWidth: maxWidth$1, className, name, clearable = false, onClear, onChange, prefix, suffix, helpMessage, errorMessage, min, max, selectOnFocus, onFocus, onBlur, ...rest }, ref) {
    const inputElement = React.useRef(null);
    const clearButtonLabel = useLocalization('clear');
    const id = useId();
    const invalid$1 = propsInvalid || !!errorMessage;
    const [value, setValue] = useControlledState({
        defaultValue,
        value: controlledValue,
    });
    const handleChange = React.useCallback((event) => {
        const { value } = event.currentTarget;
        setValue(value);
        onChange?.({ value, event });
    }, [onChange, setValue]);
    const handleClear = React.useCallback((event) => {
        const value = '';
        setValue(value);
        onClear?.({ value, event });
        inputElement.current?.focus();
    }, [onClear, setValue]);
    const handleFocus = React.useCallback((event) => {
        if (selectOnFocus) {
            const target = event.target;
            // Timeout is necessary to fix selection in Safari: https://stackoverflow.com/questions/54229359/why-does-select-not-work-on-safari-with-reactjs
            setTimeout(() => target.select(), 50);
        }
        onFocus?.();
    }, [onFocus, selectOnFocus]);
    const mergedRef = useMergeRefs([inputElement, ref]);
    const helpMessageId = `${id}-help`;
    const errorMessageId = `${id}-error`;
    const prefixId = `${id}-prefix`;
    const suffixId = `${id}-suffix`;
    const handleAppendageClick = React.useCallback((event) => {
        inputElement.current?.focus();
        // Prevent click events on content propagating to potential parents that have onClick handlers defined.
        event.stopPropagation();
    }, []);
    return (React.createElement(FormRow, { id: id, label: label, labelClassName: labelClassName, labelPosition: labelPosition, labelOffset: size, helpMessage: helpMessage, helpMessageId: helpMessageId, invalid: invalid$1, errorMessage: errorMessage, errorMessageId: errorMessageId, disabled: disabled$1, required: required, className: classnames({
            [invalid]: invalid$1,
            [disabled]: disabled$1,
            [small]: size === 'small',
            [maxWidth]: !!maxWidth$1,
        }, className) },
        React.createElement(Stack, { gap: "none", alignItems: "center", wrap: false, className: control, onClick: handleAppendageClick },
            icon$1 && React.createElement(Icon, { name: icon$1, className: icon }),
            prefix && (React.createElement("span", { className: appendage, id: prefixId }, prefix)),
            React.createElement("input", { ...rest, style: maxWidth$1 ? { width: maxWidth$1 } : undefined, ref: mergedRef, className: input, type: type, value: value, onChange: handleChange, id: id, name: name, placeholder: placeholder, autoFocus: autoFocus, autoComplete: autoComplete, min: min, max: max, onFocus: handleFocus, onBlur: onBlur, disabled: disabled$1, readOnly: readOnly, "aria-invalid": invalid$1, "aria-describedby": classnames({
                    [prefixId]: prefix,
                    [suffixId]: suffix,
                    [helpMessageId]: helpMessage,
                    [errorMessageId]: errorMessage,
                }), "aria-required": required }),
            suffix && (React.createElement("span", { className: appendage, id: suffixId }, suffix)),
            loading && React.createElement(Spinner, { size: "small", className: spinner }),
            clearable && !disabled$1 && value && (React.createElement(Button, { type: "button", iconStart: "small-circular-cross", hideLabel: true, kind: "subtle", size: "xsmall", tabIndex: -1, onClick: handleClear, className: clearButton }, clearButtonLabel)),
            labelPosition === 'hidden' && required && React.createElement(FormRequiredIndicator, { className: requiredIndicator }))));
});

export { Input };
